import { useEffect, useState, useCallback } from 'react'
import { devLog } from '../utils/devLog'

/**
 * CALLBACK REF VERSION - использует useCallback для надежного ref
 */
const useHorizontalWheel = (scrollSpeed = 1.2) => {
  const [container, setContainer] = useState(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)
  const [isMouseOver, setIsMouseOver] = useState(false)
  const [hasFocus, setHasFocus] = useState(false)

  // Touch state для swipe navigation (будет реализовано в HIGH priority)
  // const [touchStartX, setTouchStartX] = useState(null)
  // const [isTouching, setIsTouching] = useState(false)

  devLog('🎯 useHorizontalWheel: CALLBACK REF версия, container:', !!container)

  // CALLBACK REF - надежная привязка к DOM
  const containerRef = useCallback((node) => {
    devLog('🔗 containerRef callback вызван с node:', !!node, node?.tagName)
    if (node !== null) {
      devLog('✅ containerRef: DOM элемент получен!', {
        tagName: node.tagName,
        tabIndex: node.tabIndex,
        className: node.className.substring(0, 50)
      })
      setContainer(node)
    } else {
      devLog('❌ containerRef: DOM элемент удален')
      setContainer(null)
    }
  }, [])

  // Функция для обновления состояния кнопок прокрутки
  const updateScrollButtons = useCallback(() => {
    if (!container) {
      devLog('⚠️ updateScrollButtons: container не готов')
      return
    }

    const { scrollLeft, scrollWidth, clientWidth } = container
    const scrollEndThreshold = 2
    
    const newCanScrollLeft = scrollLeft > 0
    const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - scrollEndThreshold
    
    setCanScrollLeft(newCanScrollLeft)
    setCanScrollRight(newCanScrollRight)
    devLog('✅ updateScrollButtons: обновлены', { canScrollLeft: newCanScrollLeft, canScrollRight: newCanScrollRight })
  }, [container])

  // Функция для программной прокрутки
  const scrollTo = useCallback((direction) => {
    devLog('🎯 scrollTo:', direction, 'container готов:', !!container)
    if (!container) return

    const scrollAmount = container.clientWidth * 0.8
    const targetScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount

    container.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    })
    devLog('🎯 scrollTo: прокрутка применена')
  }, [container])

  // Функция для прокрутки через клавиатуру
  const keyboardScroll = useCallback((direction) => {
    devLog('⌨️ keyboardScroll:', direction, 'container готов:', !!container)
    if (!container) return

    const scrollAmount = 100
    const targetScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount

    const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, container.scrollWidth - container.clientWidth))
    container.scrollLeft = finalScrollLeft
    devLog('⌨️ keyboardScroll: прокрутка применена')
  }, [container])

  // ОСНОВНОЙ useEffect - срабатывает когда container становится доступным
  useEffect(() => {
    devLog('🔧 useEffect: container изменился:', !!container)
    
    if (!container) {
      devLog('⚠️ useEffect: container отсутствует, пропускаем инициализацию')
      return
    }

    devLog('✅ useEffect: container готов, инициализируем ВСЕ события')

    const handleScroll = () => {
      devLog('📜 handleScroll')
      updateScrollButtons()
    }
    
    const handleFocus = () => {
      devLog('🎯 handleFocus: FOCUS ПОЛУЧЕН!')
      setHasFocus(true)
    }
    
    const handleBlur = () => {
      devLog('🎯 handleBlur: FOCUS ПОТЕРЯН!')
      setHasFocus(false)
    }

    const handleKeyDown = (e) => {
      devLog('⌨️ handleKeyDown:', e.key, 'isMouseOver:', isMouseOver, 'hasFocus:', hasFocus)
      const shouldHandle = isMouseOver || hasFocus
      
      if (!shouldHandle) {
        devLog('❌ keydown: не обрабатываем')
        return
      }

      if (e.key === 'ArrowLeft') {
        devLog('⬅️ ArrowLeft!')
        e.preventDefault()
        keyboardScroll('left')
      } else if (e.key === 'ArrowRight') {
        devLog('➡️ ArrowRight!')
        e.preventDefault()
        keyboardScroll('right')
      }
    }

    const localWheelHandler = (e) => {
      devLog('🎡 wheel:', e.deltaY, 'isMouseOver:', isMouseOver, 'hasFocus:', hasFocus)
      
      if (!isMouseOver && !hasFocus) {
        devLog('❌ wheel: не обрабатываем')
        return
      }

      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        devLog('❌ wheel: горизонтальная прокрутка')
        return
      }

      devLog('✅ wheel: ПРИМЕНЯЕМ ПРОКРУТКУ!')
      e.preventDefault()
      e.stopPropagation()
      
      const delta = e.deltaY
      const scrollAmount = delta * scrollSpeed
      
      const newScrollLeft = container.scrollLeft + scrollAmount
      const finalScrollLeft = Math.max(0, Math.min(newScrollLeft, container.scrollWidth - container.clientWidth))
      
      container.scrollLeft = finalScrollLeft
      devLog('🎡 wheel: прокрутка применена')
    }

    const handleMouseEnter = () => {
      devLog('🐭 mouseEnter')
      setIsMouseOver(true)
    }
    
    const handleMouseLeave = () => {
      devLog('🐭 mouseLeave')
      setIsMouseOver(false)
    }

    devLog('🔧 Добавляем все обработчики событий к контейнеру')
    
    // Добавляем все обработчики
    container.addEventListener('scroll', handleScroll, { passive: true })
    container.addEventListener('focus', handleFocus)
    container.addEventListener('blur', handleBlur)
    container.addEventListener('keydown', handleKeyDown, { passive: false })
    container.addEventListener('wheel', localWheelHandler, { passive: false })
    container.addEventListener('mouseenter', handleMouseEnter)
    container.addEventListener('mouseleave', handleMouseLeave)

    // Инициализируем состояние кнопок
    updateScrollButtons()
    
    devLog('✅ ВСЕ обработчики добавлены успешно к контейнеру')

    return () => {
      devLog('🧹 Удаляем все обработчики')
      container.removeEventListener('scroll', handleScroll)
      container.removeEventListener('focus', handleFocus)
      container.removeEventListener('blur', handleBlur)
      container.removeEventListener('keydown', handleKeyDown)
      container.removeEventListener('wheel', localWheelHandler)
      container.removeEventListener('mouseenter', handleMouseEnter)
      container.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [container, updateScrollButtons, isMouseOver, hasFocus, scrollSpeed, keyboardScroll])

  // ResizeObserver
  useEffect(() => {
    if (!container) {
      devLog('⚠️ ResizeObserver: container не готов')
      return
    }
    
    devLog('✅ ResizeObserver: инициализация')
    
    const observer = new ResizeObserver(() => {
      devLog('📐 ResizeObserver triggered')
      updateScrollButtons()
    })

    observer.observe(container, { box: 'content-box' })
    window.addEventListener('resize', updateScrollButtons, { passive: true })
    
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateScrollButtons)
    }
  }, [container, updateScrollButtons])

  devLog('🎯 useHorizontalWheel: возвращаем объект, states:', { isMouseOver, hasFocus, hasContainer: !!container })

  return {
    containerRef, // Это теперь callback
    canScrollLeft,
    canScrollRight,
    scrollLeft: () => scrollTo('left'),
    scrollRight: () => scrollTo('right'),
    isMouseOver,
    hasFocus
  }
}

export default useHorizontalWheel
